import { supabase } from './supabase';
import { checkAndUpdateStreakData } from './streakUtils';
import {
  needsWeeklyReset, getMondayISO, getWeekKey,
  assignGroupId, getLeagueChange, calcMomentum,
} from './leagueUtils';
import { decayStats, applyBoost, LOGIN_BOOST, defaultAvatarStats } from './tamagotchiUtils';

// Default user data structure
export const getDefaultUserData = () => ({
  xp: 0,
  level: 'Med Student',
  coins: 0,
  username: '',
  avatar: {
    environment: 'blue',
    skinColor: 'blue',
    hair: null,
    hat: null,
    glasses: null,
    eyebrows: null,
    mouth: null,
    beard: null,
    top: null,
    bottom: null,
  },
  streakData: {
    currentStreak: 0,
    longestStreak: 0,
    lastLoginDate: null,
    streakBrokenAt: null,
    dailyBonusCollectedToday: false,
    totalStreakCoinsEarned: 0,
    activityDates: [],
  },
  organProgress: {
    heart: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    brain: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    lungs: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    colon: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    liver: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    kidney: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    womenreproductive: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    skeleton: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    thyroid: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    adrenalgland: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    usmle1: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    usmle2: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    genetics: { completed: [], moduleData: {}, currentModule: 0, score: 0 },
    "genetics-methods": { completed: [], moduleData: {}, currentModule: 0, score: 0 },
  },
  weeklyXP: 0,
  weeklyXpResetAt: null,
  leagueTier: 1,
  leagueGroupId: null,
  leagueWeek: null,
  leagueOptOut: false,
  avatarStats: defaultAvatarStats(),
  defeatedEnemies: [],
  medicineCards: [],
  achievements: [],
  completedModules: [],
  unlockedItems: ['blue', 'blue'],
  awards: [],
  equippedItems: {
    environment: 'blue',
    skinColor: 'blue',
    hair: null,
    hat: null,
    glasses: null,
    eyebrows: null,
    mouth: null,
    beard: null,
    top: null,
    bottom: null,
  },
});

// Load user data from database
export const loadUserData = async (userId) => {
  try {
    // Fetch user profile
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (profileError && profileError.code !== 'PGRST116') {
      throw profileError;
    }

    // Fetch avatar
    const { data: avatar, error: avatarError } = await supabase
      .from('user_avatars')
      .select('*')
      .eq('id', userId)
      .single();

    if (avatarError && avatarError.code !== 'PGRST116') {
      throw avatarError;
    }

    // Fetch organ progress
    const { data: organProgressData, error: organError } = await supabase
      .from('organ_progress')
      .select('*')
      .eq('user_id', userId);

    if (organError) throw organError;

    // Fetch inventory
    const { data: inventory, error: inventoryError } = await supabase
      .from('user_inventory')
      .select('*')
      .eq('id', userId)
      .single();

    if (inventoryError && inventoryError.code !== 'PGRST116') {
      throw inventoryError;
    }

    // If user doesn't exist, create default data
    if (!profile) {
      const defaultData = getDefaultUserData();
      await createUserData(userId, defaultData);
      return defaultData;
    }

    // Transform database data to app format
    const organProgress = {};
    const defaults = getDefaultUserData();

    // Initialize all organs with defaults
    Object.keys(defaults.organProgress).forEach(organ => {
      organProgress[organ] = defaults.organProgress[organ];
    });

    // Override with actual data from database
    if (organProgressData) {
      organProgressData.forEach(progress => {
        organProgress[progress.organ] = {
          completed: progress.completed || [],
          moduleData: progress.module_data || {},
          currentModule: progress.current_module || 0,
          score: progress.score || 0,
        };
      });
    }

    const avatarObj = avatar ? {
      environment: avatar.environment || 'blue',
      skinColor: avatar.skin_color || 'blue',
      hair: avatar.hair,
      hat: avatar.hat,
      glasses: avatar.glasses,
      eyebrows: avatar.eyebrows,
      mouth: avatar.mouth,
      beard: avatar.beard,
      top: avatar.top,
      bottom: avatar.bottom,
    } : defaults.avatar;

    // Build streak data from profile
    const streakData = {
      currentStreak: profile.current_streak || 0,
      longestStreak: profile.longest_streak || 0,
      lastLoginDate: profile.last_login_date || null,
      streakBrokenAt: profile.streak_broken_at || null,
      dailyBonusCollectedToday: profile.daily_bonus_collected_today || false,
      totalStreakCoinsEarned: profile.total_streak_coins_earned || 0,
      activityDates: profile.activity_dates || [],
    };

    // Check and update streak on load
    const streakResult = checkAndUpdateStreakData(streakData);

    // If streak changed, save it immediately
    if (streakResult.isNewDay) {
      await saveStreakData(userId, streakResult.streakData);
    }

    // Compute avatar stats: apply time-based decay, then login boost on new day
    const rawStats = {
      energy:    profile.avatar_energy    ?? 80,
      happiness: profile.avatar_happiness ?? 80,
      fullness:  profile.avatar_fullness  ?? 80,
    };
    let avatarStats = decayStats(rawStats, profile.avatar_last_updated || null);
    if (streakResult.isNewDay) {
      avatarStats = applyBoost(avatarStats, LOGIN_BOOST);
    }

    return {
      xp: profile.xp || 0,
      level: profile.level || 'Med Student',
      coins: profile.coins || 0,
      username: profile.username || '',
      avatar: avatarObj,
      organProgress,
      unlockedItems: inventory?.unlocked_items || defaults.unlockedItems,
      awards: inventory?.awards || [],
      equippedItems: avatarObj,
      streakData: streakResult.streakData,
      weeklyXP: profile.weekly_xp || 0,
      weeklyXpResetAt: profile.weekly_xp_reset_at || null,
      leagueTier: profile.league_tier || 1,
      leagueGroupId: profile.league_group_id ?? null,
      leagueWeek: profile.league_week || null,
      leagueOptOut: profile.league_opt_out || false,
      avatarStats,
      defeatedEnemies:  profile.defeated_enemies  || [],
      medicineCards:    profile.medicine_cards    || [],
      achievements:     profile.achievements      || [],
      completedModules: profile.completed_modules || [],
      _streakResult: streakResult, // Pass to App.js for bonus modal
    };
  } catch (error) {
    console.error('Error loading user data:', error);
    throw error;
  }
};

// Save streak data to user_profiles
export const saveStreakData = async (userId, streakData) => {
  try {
    const { error } = await supabase
      .from('user_profiles')
      .update({
        current_streak: streakData.currentStreak,
        longest_streak: streakData.longestStreak,
        last_login_date: streakData.lastLoginDate,
        streak_broken_at: streakData.streakBrokenAt,
        daily_bonus_collected_today: streakData.dailyBonusCollectedToday,
        total_streak_coins_earned: streakData.totalStreakCoinsEarned,
        activity_dates: streakData.activityDates || [],
      })
      .eq('id', userId);

    if (error) throw error;
  } catch (error) {
    console.error('Error saving streak data:', error);
    throw error;
  }
};

// Create initial user data in database
export const createUserData = async (userId, userData) => {
  try {
    // Insert user profile with streak data
    const { error: profileError } = await supabase
      .from('user_profiles')
      .insert({
        id: userId,
        username: userData.username || '',
        xp: userData.xp || 0,
        level: userData.level || 'Med Student',
        coins: userData.coins || 0,
        current_streak: userData.streakData?.currentStreak || 0,
        longest_streak: userData.streakData?.longestStreak || 0,
        last_login_date: userData.streakData?.lastLoginDate || null,
        streak_broken_at: userData.streakData?.streakBrokenAt || null,
        daily_bonus_collected_today: userData.streakData?.dailyBonusCollectedToday || false,
        total_streak_coins_earned: userData.streakData?.totalStreakCoinsEarned || 0,
        activity_dates: userData.streakData?.activityDates || [],
        weekly_xp: 0,
        league_tier: 1,
        league_group_id: null,
        league_opt_out: false,
        avatar_energy: 80,
        avatar_happiness: 80,
        avatar_fullness: 80,
        avatar_last_updated: null,
      });

    if (profileError) throw profileError;

    // Insert avatar
    const { error: avatarError } = await supabase
      .from('user_avatars')
      .insert({
        id: userId,
        environment: userData.avatar?.environment || 'blue',
        skin_color: userData.avatar?.skinColor || 'blue',
        hair: userData.avatar?.hair,
        hat: userData.avatar?.hat,
        glasses: userData.avatar?.glasses,
        eyebrows: userData.avatar?.eyebrows,
        mouth: userData.avatar?.mouth,
        beard: userData.avatar?.beard,
        top: userData.avatar?.top,
        bottom: userData.avatar?.bottom,
      });

    if (avatarError) throw avatarError;

    // Insert organ progress
    const organProgressInserts = Object.keys(userData.organProgress).map(organ => ({
      user_id: userId,
      organ,
      completed: userData.organProgress[organ].completed || [],
      module_data: userData.organProgress[organ].moduleData || {},
      current_module: userData.organProgress[organ].currentModule || 0,
      score: userData.organProgress[organ].score || 0,
    }));

    const { error: organError } = await supabase
      .from('organ_progress')
      .insert(organProgressInserts);

    if (organError) throw organError;

    // Insert inventory
    const { error: inventoryError } = await supabase
      .from('user_inventory')
      .insert({
        id: userId,
        unlocked_items: userData.unlockedItems || ['blue', 'blue'],
        awards: userData.awards || [],
      });

    if (inventoryError) throw inventoryError;

    return userData;
  } catch (error) {
    console.error('Error creating user data:', error);
    throw error;
  }
};

// Save user data to database
export const saveUserData = async (userId, userData) => {
  try {
    // Update user profile including streak data
    const { error: profileError } = await supabase
      .from('user_profiles')
      .update({
        username: userData.username,
        xp: userData.xp,
        level: userData.level,
        coins: userData.coins,
        current_streak: userData.streakData?.currentStreak,
        longest_streak: userData.streakData?.longestStreak,
        last_login_date: userData.streakData?.lastLoginDate,
        streak_broken_at: userData.streakData?.streakBrokenAt,
        daily_bonus_collected_today: userData.streakData?.dailyBonusCollectedToday,
        total_streak_coins_earned: userData.streakData?.totalStreakCoinsEarned,
        activity_dates: userData.streakData?.activityDates || [],
        weekly_xp: userData.weeklyXP || 0,
        avatar_energy:    userData.avatarStats?.energy    ?? 80,
        avatar_happiness: userData.avatarStats?.happiness ?? 80,
        avatar_fullness:  userData.avatarStats?.fullness  ?? 80,
        avatar_last_updated: userData.avatarStats?.lastUpdated || null,
      })
      .eq('id', userId);

    if (profileError) throw profileError;

    // Update avatar
    const { error: avatarError } = await supabase
      .from('user_avatars')
      .update({
        environment: userData.avatar?.environment || userData.equippedItems?.environment,
        skin_color: userData.avatar?.skinColor || userData.equippedItems?.skinColor,
        hair: userData.avatar?.hair || userData.equippedItems?.hair,
        hat: userData.avatar?.hat || userData.equippedItems?.hat,
        glasses: userData.avatar?.glasses || userData.equippedItems?.glasses,
        eyebrows: userData.avatar?.eyebrows || userData.equippedItems?.eyebrows,
        mouth: userData.avatar?.mouth || userData.equippedItems?.mouth,
        beard: userData.avatar?.beard || userData.equippedItems?.beard,
        top: userData.avatar?.top || userData.equippedItems?.top,
        bottom: userData.avatar?.bottom || userData.equippedItems?.bottom,
      })
      .eq('id', userId);

    if (avatarError) throw avatarError;

    // Update organ progress
    if (userData.organProgress) {
      for (const organ of Object.keys(userData.organProgress)) {
        const { error: organError } = await supabase
          .from('organ_progress')
          .update({
            completed: userData.organProgress[organ].completed || [],
            module_data: userData.organProgress[organ].moduleData || {},
            current_module: userData.organProgress[organ].currentModule || 0,
            score: userData.organProgress[organ].score || 0,
          })
          .eq('user_id', userId)
          .eq('organ', organ);

        if (organError) throw organError;
      }
    }

    // Update inventory
    const { error: inventoryError } = await supabase
      .from('user_inventory')
      .update({
        unlocked_items: userData.unlockedItems || [],
        awards: userData.awards || [],
      })
      .eq('id', userId);

    if (inventoryError) throw inventoryError;

    // Persist RPG progress to Supabase
    const { error: rpgError } = await supabase
      .from('user_profiles')
      .update({
        defeated_enemies:  userData.defeatedEnemies  || [],
        medicine_cards:    userData.medicineCards    || [],
        achievements:      userData.achievements     || [],
        completed_modules: userData.completedModules || [],
      })
      .eq('id', userId);
    if (rpgError) throw rpgError;


    return userData;
  } catch (error) {
    console.error('Error saving user data:', error);
    throw error;
  }
};

// Save only avatar stat fields (called after decay is applied on load)
export const saveAvatarStats = async (userId, avatarStats) => {
  try {
    const { error } = await supabase
      .from('user_profiles')
      .update({
        avatar_energy:    avatarStats.energy    ?? 80,
        avatar_happiness: avatarStats.happiness ?? 80,
        avatar_fullness:  avatarStats.fullness  ?? 80,
        avatar_last_updated: avatarStats.lastUpdated || null,
      })
      .eq('id', userId);
    if (error) throw error;
  } catch (error) {
    console.error('Error saving avatar stats:', error);
  }
};

// ─── League / Leaderboard functions ──────────────────────────────────────────

// Detect a new week, apply promotion/demotion, reset weekly XP, assign new group
export const applyWeeklyResetIfNeeded = async (userId, currentProfile) => {
  if (!needsWeeklyReset(currentProfile.weeklyXpResetAt)) {
    return { changed: false };
  }

  // Fetch last week's group to determine rank
  let leagueChange = 0;
  if (currentProfile.leagueGroupId !== null && currentProfile.leagueGroupId !== undefined) {
    const { data: lastGroup } = await supabase
      .from('user_profiles')
      .select('id, weekly_xp, current_streak')
      .eq('league_tier', currentProfile.leagueTier)
      .eq('league_group_id', currentProfile.leagueGroupId)
      .eq('league_opt_out', false);

    if (lastGroup && lastGroup.length > 0) {
      const sorted = [...lastGroup].sort(
        (a, b) =>
          calcMomentum(b.weekly_xp, b.current_streak) -
          calcMomentum(a.weekly_xp, a.current_streak)
      );
      const myRank = sorted.findIndex(u => u.id === userId) + 1;
      if (myRank > 0) {
        leagueChange = getLeagueChange(myRank, sorted.length, currentProfile.leagueTier);
      }
    }
  }

  const newTier    = Math.min(10, Math.max(1, (currentProfile.leagueTier || 1) + leagueChange));
  const mondayISO  = getMondayISO();
  const weekKey    = getWeekKey(mondayISO);
  const newGroupId = assignGroupId(userId, weekKey, newTier);
  const weekNum    = parseInt(weekKey.split('-W')[1], 10);

  const { error } = await supabase
    .from('user_profiles')
    .update({
      weekly_xp: 0,
      weekly_xp_reset_at: mondayISO,
      league_tier: newTier,
      league_group_id: newGroupId,
      league_week: weekNum,
    })
    .eq('id', userId);

  if (error) throw error;

  return { changed: true, newTier, leagueChange, newGroupId };
};

// Fetch the current user's league group for the leaderboard
export const loadLeaderboardData = async (userId) => {
  // Get current user's league assignment
  const { data: me, error: meError } = await supabase
    .from('user_profiles')
    .select('league_tier, league_group_id')
    .eq('id', userId)
    .single();

  if (meError) throw meError;
  if (me.league_group_id === null) return { group: [], myTier: me.league_tier || 1 };

  // Fetch all opted-in users in the same tier + group
  const { data: profiles, error: groupError } = await supabase
    .from('user_profiles')
    .select('id, username, weekly_xp, current_streak, league_tier, xp')
    .eq('league_tier', me.league_tier)
    .eq('league_group_id', me.league_group_id)
    .eq('league_opt_out', false)
    .limit(50);

  if (groupError) throw groupError;

  // Batch-fetch avatars
  const ids = (profiles || []).map(p => p.id);
  const { data: avatars, error: avatarError } = await supabase
    .from('user_avatars')
    .select('id, environment, skin_color, hair, hat, glasses, eyebrows, mouth, beard, top, bottom')
    .in('id', ids);

  if (avatarError) throw avatarError;

  const avatarMap = Object.fromEntries((avatars || []).map(a => [a.id, a]));

  const group = (profiles || []).map(p => {
    const av = avatarMap[p.id];
    return {
      id: p.id,
      name: p.username || 'Anonymous',
      xp: p.xp || 0,
      weeklyXP: p.weekly_xp || 0,
      streak: p.current_streak || 0,
      isYou: p.id === userId,
      avatar: av ? {
        environment: av.environment,
        skinColor: av.skin_color,
        hair: av.hair,
        hat: av.hat,
        glasses: av.glasses,
        eyebrows: av.eyebrows,
        mouth: av.mouth,
        beard: av.beard,
        top: av.top,
        bottom: av.bottom,
      } : null,
    };
  });

  return { group, myTier: me.league_tier || 1 };
};

// Toggle leaderboard visibility for the current user
export const updateLeagueOptOut = async (userId, optOut) => {
  const { error } = await supabase
    .from('user_profiles')
    .update({ league_opt_out: optOut })
    .eq('id', userId);
  if (error) throw error;
};
